import moment from "moment";

//  Function to get the modifiers for "react-day-picker", which represent the date range occupied by events
export const getModifiers = (calendarEvents) => {
  const toSort = calendarEvents.map((x) => x);

  //   We sort the array to get the shorter events prevailing over the larger ones
  //   So this is just a sort putting the shortest events at the end, so they get processed last
  //   See Array.sort() docs for a better understanding
  toSort &&
    toSort.sort((eventA, eventB) => {
      if (!!!eventA.toDate) return 1;
      if (!!!eventB.toDate) return -1;

      const eventADays = moment(eventA.toDate, "YYYYMMDD").diff(
        moment(eventA.fromDate, "YYYYMMDD"),
        "days"
      );
      const eventBDays = moment(eventB.toDate, "YYYYMMDD").diff(
        moment(eventB.fromDate, "YYYYMMDD"),
        "days"
      );
      if (eventADays < eventBDays) return 1;
      if (eventADays > eventBDays) return -1;
      if (eventADays === eventBDays) return 0;
    });

  const modifiers = toSort.reduce(
    //   accumulator will be an object like the following one, storing the modifiers information
    //       {
    //       modifiers: {},
    //       modifiersStyles: {},
    //       modifiersClassNames: {},
    //     }
    (accumulator, currentEvent) => {
      // Event name replacing " " with "-"
      const name = currentEvent.name.replace(/\s/g, "-").toLowerCase();

      // Check if the current event is between another event's date range
      const isBetweenAnother = calendarEvents.filter(
        (event) =>
          currentEvent.toDate &&
          event.toDate &&
          moment(currentEvent.fromDate).isBetween(
            moment(event.fromDate),
            moment(event.toDate)
          )
      ).length;

      // Check if the date is a range or individual and create its corresponding modifier
      // These are just objects like: { [eventName]: from: Date, to: Date }
      //                          or: { [eventName]: Date } if it is not a range
      const modifiers = currentEvent.toDate
        ? // If it is a range
          Object.assign({}, accumulator.modifiers, {
            [`${name}-top`]: moment(currentEvent.fromDate, "YYYYMMDD").toDate(),
            [`${name}-middle`]: [
              {
                from: moment(currentEvent.fromDate, "YYYYMMDD")
                  .add(1, "days")
                  .toDate(),
                to: moment(currentEvent.toDate, "YYYYMMDD")
                  .subtract(1, "days")
                  .toDate(),
              },
            ],
            [`${name}-bottom`]: moment(
              currentEvent.toDate,
              "YYYYMMDD"
            ).toDate(),
          })
        : // If it is just an individual date
          Object.assign({}, accumulator.modifiers, {
            [name]: moment(currentEvent.fromDate, "YYYYMMDD").toDate(),
          });
      // This color property makes contrast with the background color of the event (black | white)
      const color = invertColor(currentEvent.eventColor);

      // Same thing than modifiers, but here we apply styles
      // ["*-top"] / ["*-bottom"] properties modify the rounded corners of the starting/ending dates of ranges
      const modifiersStyles = currentEvent.toDate
        ? Object.assign({}, accumulator.modifiersStyles, {
            [`${name}-middle`]: {
              color: color,
              backgroundColor: currentEvent.eventColor,
              borderRadius: 0,
              transition: "all .5s",
            },
            [`${name}-top`]: {
              color: color,
              backgroundColor: currentEvent.eventColor,
              borderRadius: `${isBetweenAnother ? "0 0" : "100% 100%"} 0 0`,
              transition: "all .5s",
            },
            [`${name}-bottom`]: {
              color: color,
              backgroundColor: currentEvent.eventColor,
              borderRadius: `0 0 ${isBetweenAnother ? "0 0" : "100% 100%"}`,
              transition: "all .5s",
            },
          })
        : // Just and individual date styles
          Object.assign({}, accumulator.modifiersStyles, {
            [name]: {
              color: color,
              backgroundColor: `${currentEvent.eventColor}`,
              transition: "all .5s",
            },
          });

      // Also getting some classnames to modify through css
      const modifiersClassNames = currentEvent.toDate
        ? Object.assign({}, accumulator.modifiersClassNames, {
            [`${name}-middle`]: `calendar-custom-modifier middle ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
            [`${name}-top`]: `calendar-custom-modifier top ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
            [`${name}-bottom`]: `calendar-custom-modifier bottom ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
          })
        : Object.assign({}, accumulator.modifiersClassNames, {
            [`${name}`]: `calendar-custom-modifier single ${name}${
              isBetweenAnother ? " is-between" : ""
            }`,
          });

      return { modifiers, modifiersStyles, modifiersClassNames };
    },
    // Initial state of the accumulator
    {
      modifiers: {},
      modifiersStyles: {},
      modifiersClassNames: {},
    }
  );

  return modifiers;
};

//  Function to determinate if the text color should be black or white
export const invertColor = (hex, forFilter = false) => {
  if (hex.indexOf("#") === 0) {
    hex = hex.slice(1);
  }

  const r = parseInt(hex.slice(0, 2), 16),
    g = parseInt(hex.slice(2, 4), 16),
    b = parseInt(hex.slice(4, 6), 16);

  const lum = r * 0.299 + g * 0.587 + b * 0.114;

  const result = forFilter
    ? lum > 150
      ? "var(--black-filter)"
      : "var(--filter-white)"
    : lum > 150
    ? "var(--black-always)"
    : "var(--white-always)";
  return result;
};

// Function to sort the annual planner calendar header by the coach first day of the week preference
export const getSortedWeekDays = (startingCalendarDay) => {
  const weekDays = [
    { day: "Sunday", index: 0 },
    { day: "Monday", index: 1 },
    { day: "Tuesday", index: 2 },
    { day: "Wednesday", index: 3 },
    { day: "Thursday", index: 4 },
    { day: "Friday", index: 5 },
    { day: "Saturday", index: 6 },
  ];

  while (weekDays[0].index < startingCalendarDay) {
    weekDays.push(weekDays.shift());
  }

  return weekDays;
};

// Function to get random color for the add event modal
export const getRandomColor = () =>
  `#${Math.floor(Math.random() * 16777215)
    .toString(16)
    .padStart(6, 0)}`;

// Also a function to get random color for the add event modal, but pastel colors
export const getRandomPastelColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = 80;
  const l = 65;

  const hDecimal = l / 100;
  const a = (s * Math.min(hDecimal, 1 - hDecimal)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = hDecimal - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

    // Convert to Hex and prefix with "0" if required
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// This function is used to serve the hidden days for the react-day-picker
// It is used to calculate the days that are hidden in the calendar
// It is used to calculate the number of weeks that we will display in the current month
export const getColumnMonthData = (month, startingCalendarDay = 0) => {
  const lastMonthDay = moment(month).endOf("month");
  // The amount of days that we have to sum/subtract to adjust to the starting calendar day
  const endAmount = getStartingCalendarDayAdjustment(
    lastMonthDay,
    startingCalendarDay
  );

  const lastWeekDay = moment(lastMonthDay).endOf("week");

  // Here we decide if we have to hide or not the last week of the month
  // This happens when the following month starts in the last week of the current one
  // So we always want to have the first day of the month in the first week of the month;
  // not in the last week of the previous month
  const hiddenDays =
    // If the last day of the last week of the month belongs to the following month, then we have to hide that week
    moment(lastWeekDay).add(endAmount, "days").month() !== moment(month).month()
      ? [
          {
            from: moment(lastWeekDay)
              .startOf("week")
              .add(endAmount, "days")
              .toDate(),
            to: moment(lastWeekDay).add(endAmount, "days").toDate(),
          },
        ]
      : [];

  // Also getting the first day of the month, and the days amount necessary for the starting calendar day adjustment
  const firstMonthDay = moment(month).startOf("month");
  const startAmout = getStartingCalendarDayAdjustment(
    firstMonthDay,
    startingCalendarDay
  );

  // Here we get the number of days of the current month. We subtract 7 if there is a hidden week
  const numberOfDays = hiddenDays.length
    ? moment(lastWeekDay)
        .add(endAmount, "days")
        .diff(firstMonthDay.startOf("week").add(startAmout, "days"), "days") +
      1 -
      7
    : moment(lastWeekDay)
        .add(endAmount, "days")
        .diff(firstMonthDay.startOf("week").add(startAmout, "days"), "days") +
      1;

  // Dividing the amount of days by 7 to get the number of weeks
  const numberOfWeeks = numberOfDays / 7;
  return [numberOfWeeks, hiddenDays];
};

// Function to get the first day of the passed week
export const getWeekColumnData = (month, startingCalendarDay, weekNumber) => {
  const weekFirstDay = moment(month).startOf("month");

  // Amount of days needed for the starting calendar day adjustment
  const amount = getStartingCalendarDayAdjustment(
    weekFirstDay,
    startingCalendarDay
  );

  weekFirstDay.startOf("week").add(amount, "days").add(weekNumber, "weeks");
  return weekFirstDay;
};

// Tool for getting the amount of days needed to adjust the starting calendar day for momentjs dates
export const getStartingCalendarDayAdjustment = (
  dayReference,
  startingCalendarDay
) => {
  const amount =
    startingCalendarDay > 0
      ? moment(dayReference).day() < startingCalendarDay
        ? -(7 - startingCalendarDay)
        : startingCalendarDay
      : 0;
  return amount;
};

// Getting the default template phases
export const getDefaultPhases = (phasesTemplates) => {
  const defaultPhases =
    (phasesTemplates &&
      phasesTemplates.templates &&
      phasesTemplates.defaultTemplate &&
      phasesTemplates.templates.find(
        (template) => template._id === phasesTemplates.defaultTemplate
      )) ||
    [];

  if (typeof defaultPhases === "object") return defaultPhases.phases;
  return defaultPhases;
};

export const getFooterData = (calendarEvents, phasesDescriptions) => {
  let upcomingEvents = calendarEvents
    ? Array.from(calendarEvents).sort((a, b) => {
        if (
          moment(a.fromDate, "YYYYMMDD").isBefore(
            moment(b.fromDate, "YYYYMMDD")
          )
        )
          return -1;
        if (
          moment(b.fromDate, "YYYYMMDD").isBefore(
            moment(a.fromDate, "YYYYMMDD")
          )
        )
          return 1;
        return 0;
      })
    : [];

  upcomingEvents = upcomingEvents.length
    ? upcomingEvents.filter(
        (event) =>
          !moment(event.fromDate, "YYYYMMDD").isBefore(
            moment(moment.utc().format("YYYYMMDD"), "YYYYMMDD")
          )
      )
    : upcomingEvents;

  const nextEvent = upcomingEvents.shift();

  let upcomingComments = phasesDescriptions
    ? Array.from(phasesDescriptions).sort((a, b) => {
        if (
          moment(a.dateAttachment, "YYYYMMDD").isBefore(
            moment(b.dateAttachment, "YYYYMMDD")
          )
        )
          return -1;
        if (
          moment(b.dateAttachment, "YYYYMMDD").isBefore(
            moment(a.dateAttachment, "YYYYMMDD")
          )
        )
          return 1;
        return 0;
      })
    : [];

  // Here we compare against the first day of the week, cause the comments are attached to the training phases,
  // that start by the first day of the week
  const currentWeekFirstDay = moment
    .utc()
    .startOf("week")
    .add(getStartingCalendarDayAdjustment(), "days");

  upcomingComments = upcomingComments.length
    ? upcomingComments.filter(
        (phaseDescription) =>
          !moment(phaseDescription.dateAttachment, "YYYYMMDD").isBefore(
            moment(currentWeekFirstDay)
          )
      )
    : upcomingComments;

  const firstPhaseDescription = upcomingComments && upcomingComments.shift();
  const secondPhaseDescription = upcomingComments && upcomingComments.shift();

  return {
    upcomingEvents,
    nextEvent,
    upcomingComments,
    firstPhaseDescription,
    secondPhaseDescription,
  };
};

// Custom set interval, used to blink the event
// It passes whatever arguments are required to the callback
export const setIntervalX = (callback, delay, repetitions, ...args) => {
  let iteration = 1;
  let intervalID = window.setInterval(() => {
    callback(args, iteration);

    if (++iteration > repetitions) {
      window.clearInterval(intervalID);
    }
  }, delay);
};

export const getWeeklyStatsAvg = (
  weekStats,
  weekVolumes,
  previousWeekStats = [],
  units = "metric"
) => {
  const readiness = [];
  const sessionRPE = [];
  const bodyWeight = [];
  const kCal = [];
  const steps = [];
  const sleep = [];

  // The weekly volumes are just a sum and not an average
  let volumes =
    weekVolumes &&
    weekVolumes.reduce((acc, current) => acc + current.volume, 0);

  // We iterate the array of the current week stats
  weekStats.forEach((stat) => {
    // If the stat has readiness, we sum all the fields of the readiness and push it to the readiness array.
    // We do the same with each stat object of the week, and at the end we divide the result by the readiness array length
    stat.readiness &&
      readiness.push(
        Object.entries(stat.readiness).reduce(
          (acc, [key, value]) => acc + value,
          0
        )
      );

    // If the stat has sessionRPE info, we push it to the array
    stat.sessionRPE && sessionRPE.push(stat.sessionRPE);
    // If the stat has body weight info, we push it to the array
    stat.bodyWeight && bodyWeight.push(stat.bodyWeight);

    // If the stat has nutrients info, we calculate the kCal amount, and push it to the array
    if (stat.nutrients) {
      const proteinsKcal = stat.nutrients.proteins
        ? Math.round(stat.nutrients.proteins * 4)
        : 0;
      const carbsKcal = stat.nutrients.carbs
        ? Math.round(stat.nutrients.carbs * 4)
        : 0;
      const fatsKcal = stat.nutrients.fats
        ? Math.round(stat.nutrients.fats * 9)
        : 0;

      const calories = Math.round(proteinsKcal + carbsKcal + fatsKcal);
      kCal.push(calories);
    }
    // Also if the stat has steps, we push it to the array
    stat.steps && steps.push(stat.steps);

    // For when sleep is implemented, for now it won't push anything
    stat.sleep && sleep.push(stat.sleep);
  });

  // Once we iterated over all the current week stats, we divide the sums by the lenght of the arrays
  // and get the avg of the week. Same with all, except the volume which is just a sum

  const readinessAvg = readiness.length
    ? Math.round(
        readiness.reduce((acc, value) => acc + value) / readiness.length
      )
    : 0;

  const sessionRPEAvg = sessionRPE.length
    ? Math.round(
        sessionRPE.reduce((acc, value) => acc + value) / sessionRPE.length
      )
    : 0;

  // Bodyweight multiplier in case the user has imperial system
  const bodyWeightMultipler = units === "metric" ? 1 : 2.205;

  const bodyWeightAvg = bodyWeight.length
    ? Math.round(
        (bodyWeight.reduce((acc, value) => acc + value) / bodyWeight.length) *
          bodyWeightMultipler
      )
    : 0;

  let bodyWeightDiff = 0;

  if (previousWeekStats.length) {
    const previousWeight = [];

    previousWeekStats.forEach((stat) => {
      stat.bodyWeight && previousWeight.push(stat.bodyWeight);
    });

    const previousWeightAvg = previousWeight.length
      ? Math.round(
          (previousWeight.reduce((acc, value) => acc + value) /
            previousWeight.length) *
            bodyWeightMultipler
        )
      : 0;

    bodyWeightDiff = previousWeight
      ? Math.round(bodyWeightAvg - previousWeightAvg)
      : 0;
  }

  let kCalAvg = kCal.length
    ? Math.round(kCal.reduce((acc, value) => acc + value) / kCal.length)
    : 0;

  let stepsAvg = steps.length
    ? Math.round(steps.reduce((acc, value) => acc + value) / steps.length)
    : 0;

  const sleepAvg = sleep.length
    ? Math.round(sleep.reduce((acc, value) => acc + value) / sleep.length)
    : 0;

  // Turning 1000 values into 1k
  volumes =
    volumes > 10000
      ? // If values are higher than 10k, then we round it to nearest thousand
        `${Math.trunc(volumes / 1000)}k`
      : volumes > 1000
      ? `${Math.round((volumes / 1000) * 10) / 10}k`
      : volumes;
  // Turning 1000 values into 1k
  stepsAvg =
    stepsAvg > 10000
      ? // If values are higher than 10k, then we round it to nearest thousand
        `${Math.trunc(stepsAvg / 1000)}k`
      : stepsAvg > 1000
      ? `${Math.round((stepsAvg / 1000) * 10) / 10}k`
      : stepsAvg;
  // Turning 1000 values into 1k
  kCalAvg =
    kCalAvg > 10000
      ? // If values are higher than 10k, then we round it to nearest thousand
        `${Math.trunc(kCalAvg / 1000)}k`
      : kCalAvg > 1000
      ? `${Math.round((kCalAvg / 1000) * 10) / 10}k`
      : kCalAvg;

  return {
    volumes,
    readinessAvg,
    sessionRPEAvg,
    bodyWeightAvg,
    kCalAvg,
    stepsAvg,
    sleepAvg,
    bodyWeightDiff,
  };
};
